import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { productsAPI, categoriesAPI } from '../lib/api';
import { useCart } from '../contexts/CartContext';
import { BottomNav } from './MarketplaceHome';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ShoppingCart,
  Search,
  Filter,
  Grid,
  List,
  ChevronDown,
  Plus
} from 'lucide-react';

export const ProductsList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addItem, itemCount } = useCart();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('categoria') || '');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    loadData();
  }, [selectedCategory, searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        productsAPI.list({
          category_id: selectedCategory || undefined,
          search: searchParams.get('q') || undefined,
          limit: 50
        }),
        categoriesAPI.list()
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/produtos?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
    toast.success(`${product.name} adicionado ao carrinho!`);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="products-list-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="font-semibold text-gray-900">Produtos</h1>
          <button onClick={() => navigate('/carrinho')} className="p-2 -mr-2 relative" data-testid="cart-btn">
            <ShoppingCart className="w-6 h-6 text-gray-700" />
            {itemCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                {itemCount}
              </span>
            )}
          </button>
        </div>
        
        {/* Search */}
        <div className="px-4 pb-3">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar produtos..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="search-input"
              />
            </div>
          </form>
        </div>
        
        {/* Categories Filter */}
        <div className="px-4 pb-3 flex gap-2 overflow-x-auto hide-scrollbar">
          <button
            onClick={() => setSelectedCategory('')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              !selectedCategory ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
            data-testid="category-all"
          >
            Todos
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
              data-testid={`category-${cat.id}`}
            >
              {cat.icon} {cat.name}
            </button>
          ))}
        </div>
      </header>

      {/* Results Info */}
      <div className="px-4 py-3 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {products.length} produto(s) encontrado(s)
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400'}`}
          >
            <Grid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400'}`}
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Products Grid/List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 px-6">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Search className="w-10 h-10 text-gray-400" />
          </div>
          <p className="text-gray-500 text-center">Nenhum produto encontrado</p>
        </div>
      ) : (
        <div className={`px-4 ${viewMode === 'grid' ? 'grid grid-cols-2 gap-3' : 'space-y-3'}`}>
          {products.map((product) => (
            viewMode === 'grid' ? (
              <Link
                key={product.id}
                to={`/produto/${product.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-sm"
                data-testid={`product-card-${product.id}`}
              >
                <div className="relative">
                  <img
                    src={product.images?.[0] || 'https://via.placeholder.com/200x200?text=Produto'}
                    alt={product.name}
                    className="w-full aspect-square object-cover"
                  />
                  {product.promo_price && (
                    <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                      OFERTA
                    </span>
                  )}
                  <button
                    onClick={(e) => handleAddToCart(e, product)}
                    className="absolute bottom-2 right-2 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg"
                    data-testid={`add-cart-${product.id}`}
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-3">
                  <h3 className="font-medium text-gray-900 text-sm truncate">{product.name}</h3>
                  <p className="text-xs text-gray-500 truncate">{product.store_name}</p>
                  <div className="mt-2">
                    {product.promo_price ? (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 line-through text-xs">{formatPrice(product.price)}</span>
                        <span className="font-bold text-green-600">{formatPrice(product.promo_price)}</span>
                      </div>
                    ) : (
                      <span className="font-bold text-gray-900">{formatPrice(product.price)}</span>
                    )}
                  </div>
                </div>
              </Link>
            ) : (
              <Link
                key={product.id}
                to={`/produto/${product.id}`}
                className="bg-white rounded-xl p-4 flex gap-4 shadow-sm"
                data-testid={`product-card-${product.id}`}
              >
                <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 relative">
                  <img
                    src={product.images?.[0] || 'https://via.placeholder.com/100x100?text=Produto'}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  {product.promo_price && (
                    <span className="absolute top-1 left-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                      OFERTA
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">{product.name}</h3>
                  <p className="text-sm text-gray-500">{product.store_name}</p>
                  <div className="mt-2">
                    {product.promo_price ? (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 line-through text-sm">{formatPrice(product.price)}</span>
                        <span className="font-bold text-lg text-green-600">{formatPrice(product.promo_price)}</span>
                      </div>
                    ) : (
                      <span className="font-bold text-lg text-gray-900">{formatPrice(product.price)}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => handleAddToCart(e, product)}
                  className="self-center w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white"
                  data-testid={`add-cart-${product.id}`}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </Link>
            )
          ))}
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default ProductsList;
